/* eslint-disable max-len */
import { Box, Card, Grid, styled, Tooltip, useMediaQuery, Skeleton, Container } from "@mui/material";

import { useTheme } from "@mui/material/styles";
import moment from "moment";
import { useContext, useEffect, useState } from "react";
import { useNavigate, generatePath } from "react-router-dom";
import { ActionBtn, AddressLink, TableComponent, Text } from "../../components";
import { AppRoutes, landDetailLink } from "../../config";
import { AppContext } from "../../contexts";
import useAuthorization from "../../contexts/useAuthorization";
import { useProfileDataCache } from "../../contexts/ProfileDataCache";
import { EtTransactionType } from "../../utils/constants";
import { formatValue } from "../../utils/formatValue";
import { sizing, sixDigits, toReadable } from "../../utils/methods";
import {
  ClickableContainer,
  hashCol,
  transactionFromCol,
  transactionToCol,
  transactionTypeCol,
  transactionValueCol,
  TransactionMobileSchema,
  Link,
  Img,
  TransactionsSchema,
  MobileEcoBankLandsSchema,
} from "../../utils/table-schemas";
import { TabsRow } from "../EcoBank/TrustWallet";
import Profile from "./index";
import ProfilePicture from "./verification/profile-picture";
// import { AdminPanelSettings as AdminIcon } from '@mui/icons-material';
import { createSvgIcon } from "@mui/material/utils";
import {
  GlobalTrustAdminIcon,
  GuardianIcon,
  PatronIcon,
  PioneerIcon,
  TrustAdminIcon,
  TrusteeIcon,
} from "../../components/RoleIcons";
import OrgPopUp from "../../components/OrgPopUp";
import { TableWithTabs } from "../EcoBank/UserWallet";
import { EmptyBox, EmptyBoxNew } from "./GuardianLands";
import { FilterBlock } from "./Verifications";
import { fromWei } from "web3-utils";
import { getTrusteeSchema } from "../Profile/Portal";

const Dashboard = () => {
  const appContext = useContext(AppContext);
  const { user, isTrust, isAdmin, isGlobalTrustAdmin, isGuardian, isPioneer } = useAuthorization();
  const { profileCache, getCachedData } = useProfileDataCache();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(560));
  const navigator = useNavigate();

  const [treeBalance, setTreeBalance] = useState<any>("");
  const [rewardsClaimed24h, setRewardsClaimed24h] = useState<any>("");
  const [adminVerifiedTrusts, setAdminVerifiedTrusts] = useState<any[]>([]);
  const [latestLandImage, setLatestLandImage] = useState<any>();
  const [isLoading, setIsLoading] = useState(false);
  const [orgModalOpen, setOrgModalOpen] = useState(false);
  const [orgData, setOrgData] = useState<any>(null);
  const [orgLands, setOrgLands] = useState<any>(null);
  const [profileData, setProfileData] = useState<any>(null);
  const [infoLoading, setInfoLoading] = useState(true);
  const [transactions, setTransactions] = useState(0);
  const [landsBought, setLandsBought] = useState(0);
  const [landsScanned, setLandsScanned] = useState(0);
  const [activeTab, setActiveTab] = useState<"portfolio" | "lands" | "transactions">("transactions");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedMenuId, setSelectedMenuId] = useState<string | null>(null);
  const verifiedByData: any[] = getCachedData("verifiedBy") || [];

  useEffect(() => {
    if (user) {
      // Set loading state based on cache status
      setInfoLoading(profileCache.isLoading);
      setIsLoading(profileCache.isLoading);

      // Wait for cache to finish loading before processing data
      if (profileCache.isLoading) {
        console.log("Dashboard: Cache is loading, waiting...");
        return;
      }

      // Get cached wallet data
      const cachedWalletData = getCachedData("walletData");
      if (cachedWalletData) {
        console.log("Dashboard: Using cached wallet data", { cachedWalletData });

        // Update state from cached wallet data
        if (cachedWalletData?.trusteeVerifications) {
          setOrgLands(cachedWalletData.trusteeVerifications);
        }
        if (cachedWalletData?.organization) {
          setOrgData(cachedWalletData.organization);
        }
        if (cachedWalletData) {
          setProfileData(cachedWalletData);
          console.log(cachedWalletData?.role);

          cachedWalletData?.organization?.secondaryWallets.forEach((secondaryWallet: any) => {
            if (cachedWalletData?.name === secondaryWallet?.name) {
              setProfileData(secondaryWallet);
              console.log({ secondaryWallet });
            }
          });
        }
        if (cachedWalletData.transactions) {
          setTransactions(cachedWalletData.transactions);
        }
        if (cachedWalletData?.portfolio?.totalVerifiedLandsCount || cachedWalletData?.guardingTokens) {
          setLandsScanned(cachedWalletData?.portfolio?.totalVerifiedLandsCount || cachedWalletData?.guardingTokens);
        }
        if (cachedWalletData.boughtLands) {
          setLandsBought(cachedWalletData.boughtLands);
        }
        if (cachedWalletData?.adminVerifiedTrusts) {
          setAdminVerifiedTrusts(cachedWalletData.adminVerifiedTrusts);
        }

        setInfoLoading(false);
      }

      // Get cached land data for latest image functionality
      const cachedTrusteeTokens = getCachedData("trusteeTokens") || [];
      const cachedBoughtLands = getCachedData("boughtLands") || [];
      const cachedMintedTokens = getCachedData("mintedTokens") || [];

      // Combine all lands for sorting
      const allLands = [...cachedTrusteeTokens, ...cachedBoughtLands, ...cachedMintedTokens];
      if (allLands.length > 0) {
        console.log("Dashboard: Using cached land data", { allLands });
        const sortedLands = allLands.sort((a: any, b: any) => (b?.ecoScore || 0) - (a?.ecoScore || 0));
        console.log({ sortedLands });
        setLatestLandImage(sortedLands[0]?.content);
      }

      // Calculate 24h rewards from cached transaction data
      const cachedTransactionData = getCachedData("transactionData") || [];
      console.log({ cachedTransactionData });
      calculate24hRewards(cachedTransactionData);

      // Still fetch tree balance as it requires blockchain interaction
      fetchTreeBalance();

      // Set default active tab based on user role and available data
      if (isAdmin) {
        setActiveTab("lands"); // Admin sees "Provenance" in lands tab
      } else {
        setActiveTab("transactions");
      }

      setIsLoading(false);
    }
  }, [user, profileCache.isLoading]); // Removed profileCache.lastFetched to prevent infinite loops

  // Memoize the portfolio guardian status column - must be before any conditional returns
  // const portfolioGuardianStatusCol = useMemo(() => createPortfolioGuardianStatusCol(library), [library]);

  // const assetsGuardianStatusCol = useMemo(() => createAssetsGuardianStatusCol(library), [library]);

  if (!user) {
    return null;
  }

  const fetchTreeBalance = () => {
    setTreeBalance(
      <Text numeric medium>
        Loading...
      </Text>
    );

    try {
      // const signer = await getMetaMaskSigner();
      // const treeContract = getContract("tree-token", signer);
      if (user?.treeBalance !== undefined && user?.treeBalance !== null) {
        try {
          const rawTreeBalance = user.treeBalance.toString();
          console.log("Tree balance (raw):", rawTreeBalance);

          // Handle "0" explicitly to avoid conversion issues
          if (rawTreeBalance === "0" || rawTreeBalance === "") {
            setTreeBalance(
              <Text medium numeric bold>
                0
              </Text>
            );
            return;
          }

          const readableBalance = fromWei(rawTreeBalance, "ether");
          console.log("Tree balance (readable):", readableBalance);

          const normalizedBalance = Number(readableBalance);
          if (isNaN(normalizedBalance)) {
            throw new Error(`Failed to normalize balance: ${readableBalance}`);
          }

          const formattedBalance = formatValue(sixDigits(normalizedBalance), true);
          console.log("Tree balance (formatted):", formattedBalance);

          let value = 0;
          let valueText = "";

          // Get rate from context instead of making additional API calls
          const rate = appContext.mintRate;
          if (rate) {
            value = normalizedBalance * rate;
            console.log({ balance: normalizedBalance, rate, sum: normalizedBalance * rate });
            valueText = `(@ $${formatValue(rate, true, 4)} / EFT)`;
          }
          const description = "$" + `${formatValue(value, true, 2)}` + " " + valueText;
          setTreeBalance(
            <Tooltip title={description} placement="top-start">
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: { xs: "end", md: "baseline" } }}>
                <Text medium numeric bold>
                  {formattedBalance}{" "}
                  {/* <Text sx={{ ml: 3 }} small>
                    {TREE_CURRENCY}
                  </Text> */}
                </Text>
                {/* <Text numeric small>
                  ${formatValue(value, true, 2)} {valueText}
                </Text> */}
              </Box>
            </Tooltip>
          );
        } catch (conversionError) {
          console.error("Error converting tree balance:", conversionError);
          setTreeBalance(
            <Text numeric medium>
              {user.treeBalance || "0"}
            </Text>
          );
        }
      } else {
        setTreeBalance(
          <Text numeric medium>
            0
          </Text>
        );
      }
    } catch (error) {
      console.error("Error fetching tree balance:", error);
      setTreeBalance(
        <Text numeric medium>
          Failed to fetch
        </Text>
      );
    }
  };

  const calculate24hRewards = (transactions: any[]) => {
    setRewardsClaimed24h(
      <Text numeric medium>
        Loading...
      </Text>
    );

    try {
      // Get current time and 24 hours ago
      const now = new Date();
      const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

      // Filter transactions from past 24 hours with type "reward_claimed"
      const rewardTransactions = transactions.filter((txn: any) => {
        const txnDate = new Date(txn.createdAt);
        return txn.type === EtTransactionType.REWARD_CLAIMED && txnDate >= twentyFourHoursAgo;
      });

      if (rewardTransactions.length === 0) {
        // No transactions in past 24h, don't display anything
        setRewardsClaimed24h(null);
        return;
      }

      // Sum up all rewards from the transactions
      let totalRewards = 0;
      rewardTransactions.forEach((txn: any) => {
        const data = txn || {};
        // Convert wei to readable number and sum
        totalRewards += data.txValue;
      });

      // Display the total if greater than 0
      if (totalRewards > 0) {
        setRewardsClaimed24h(
          <Text numeric small sx={{ fontSize: { xs: "12px", md: "14px" }, color: "#2b3501", fontWeight: "500" }}>
            +{toReadable(totalRewards, false, true, 2)} TR3 (24h)
          </Text>
        );
      } else {
        setRewardsClaimed24h(null);
      }
    } catch (error) {
      console.error("Error calculating 24h rewards:", error);
      setRewardsClaimed24h(null);
    }
  };

  if (!user?.emailVerified) {
    return (
      <Profile>
        <EmptyBoxNew
          sx={{
            marginTop: { xs: "0px", sm: "0px" },
            borderRadius: { xs: "15px", sm: "20px" },
            padding: { xs: "16px", sm: "24px" },
            height: "fit-content",
          }}
        >
          <Text
            capital={false}
            sx={{
              font: "inherit",
              fontSize: { xs: "20px", sm: "22px", md: "26px" },
              fontWeight: "600",
              color: "black",
              width: { xs: "100%", md: "50%" },
              lineHeight: { xs: 1.3, md: 1.5 },
            }}
          >
            Important
          </Text>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "column" },
              gap: { xs: "12px", sm: "16px", md: "24px" },
              padding: { xs: "8px", sm: "12px", md: "0px" },
              width: "100%",
              justifyContent: "space-between",
              height: "100%",
            }}
          >
            <Box
              sx={{
                flex: { xs: "1", md: "1" },
                display: "flex",
                flexDirection: "column",
                gap: { xs: "6px", sm: "8px", md: "12px" },
              }}
            >
              Please complete your account setup and verify your email to get your dashboard activated.
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: { xs: "center", md: "flex-end" },
                width: "100%",
                borderTop: "1px solid black",
                marginTop: { xs: "4px", sm: "8px", md: "16px" },
                padding: { xs: "10px", sm: "15px", md: "20px 20px 0px 0px" },
              }}
            >
              <ActionBtn
                sx={{ alignSelf: "end", background: "black", color: "white" }}
                onClick={() => navigator(AppRoutes.Profile.subRoutes.Identification.path)}
              >
                Get started
              </ActionBtn>
            </Box>
          </Box>
        </EmptyBoxNew>
      </Profile>
    );
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>, data: any) => {
    setSelectedMenuId(data.id);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setSelectedMenuId(null);
    setAnchorEl(null);
  };

  const handleOrgClick = (e: React.MouseEvent<HTMLElement>, data: any) => {
    e.stopPropagation();
    const org = data?.organization;
    if (!org) return;
    setOrgData(org);

    // Resolve the lands OrgPopUp's hero card needs. `organization.verifiedLands`
    // entries are thin (only `name`/`image`/`meta`/`ecoScore` — no `content`/`etNft`),
    // while the rich land objects (with `content` + `etNft`) live in each secondary
    // wallet's `portfolio.claimedLands`. Build a lookup of the rich objects and use
    // them to enrich the verified lands so the hero card renders fully.
    const richLands: any[] = [
      ...(Array.isArray(org?.secondaryWallets)
        ? org.secondaryWallets.flatMap((w: any) =>
            Array.isArray(w?.portfolio?.claimedLands) ? w.portfolio.claimedLands : []
          )
        : []),
      ...(Array.isArray(data?.portfolio?.claimedLands) ? data.portfolio.claimedLands : []),
    ];
    const richById = new Map<any, any>(richLands.map((l: any) => [l?.id, l]));

    const verifiedLands = Array.isArray(org?.verifiedLands) ? org.verifiedLands : [];
    const enrichedVerified = verifiedLands.map((vl: any) => richById.get(vl?.id) ?? vl);

    const orgLandsResolved = enrichedVerified.length > 0 ? enrichedVerified : richLands.length > 0 ? richLands : null;
    setOrgLands(orgLandsResolved);
    setOrgModalOpen(true);
  };

  const handleModalOpen = () => {
    navigator(AppRoutes.Profile.subRoutes.AdminPortal.path);
  };

  const blockTrustee = () => {
    navigator(AppRoutes.Profile.subRoutes.AdminPortal.path);
  };

  const noop = () => {};

  const trusteeSchema = getTrusteeSchema({
    isSmallScreen: isMobile,
    isLoading,
    anchorEl,
    selectedMenuId,
    navigator,
    handleOrgClick,
    handleClick,
    handleClose,
    handleModalOpen,
    blockTrustee,
    setApproveOpen: noop,
  });

  const adminThumbCol = {
    id: "content",
    label: "Image",
    sx: { width: sizing(55) },
    headerStyle: { width: sizing(55) },
    skeleton: () => {
      return <Skeleton sx={{ width: sizing(50), height: sizing(50) }} />;
    },
    render: (txn: any) => {
      const isReassignLand = Boolean(txn?.type === "reassign_land");
      const userImage =
        txn?.type === EtTransactionType?.TRUST_DISMISSED ||
        txn?.type === EtTransactionType?.TRUST_FLAGGED_FOR_DISMISSAL ||
        txn?.type === EtTransactionType?.TRUST_ROLE_GRANTED ||
        txn?.type === EtTransactionType?.TRUST_ADMIN_ADDED ||
        txn?.type === EtTransactionType?.GLOBAL_ADMIN_ADDED;

      switch (true) {
        case isReassignLand:
          return (
            <Link
              sx={{
                width: sizing(50),
                height: sizing(50),
                margin: "0",
                cursor: txn?.nftId ? "pointer" : "not-allowed",
                pointerEvents: txn?.nftId ? "auto" : "none",
              }}
              to={landDetailLink(txn?.etNft?.ecoToken?.id || "")}
              aria-disabled={isReassignLand}
            >
              <Img
                alt="Thumb"
                sx={{ width: "100%", objectFit: "cover", height: "100%", borderRadius: 0 }}
                src={txn?.data?.landImage}
              />
            </Link>
          );

        case userImage:
          return (
            <Link
              sx={{ width: sizing(50), height: sizing(50), margin: "0" }}
              to={generatePath(AppRoutes.UserWallet.path, { address: txn?.to || "" })}
            >
              <Img
                alt="Thumb"
                sx={{
                  width: "100%",
                  objectFit:
                    txn?.type === EtTransactionType?.TRUST_ADMIN_ADDED ||
                    txn?.type === EtTransactionType?.GLOBAL_ADMIN_ADDED
                      ? "contain"
                      : "cover",
                  height: "100%",
                  borderRadius: 100,
                }}
                src={txn.toUserDetails?.profilePicture}
              />
            </Link>
          );

        default:
          return (
            <Link
              sx={{ width: sizing(50), height: sizing(50), margin: "0" }}
              to={landDetailLink(txn?.etNft?.ecoToken?.id || "")}
            >
              <Img
                alt="Thumb"
                sx={{ width: "100%", objectFit: "cover", height: "100%", borderRadius: 0 }}
                src={txn?.etNft?.ecoToken?.content}
              />
            </Link>
          );
      }
    },
  };

  const orgDetails = {
    id: "orgName",
    label: "Name",
    sx: { minWidth: { xs: 105, md: 130 } },
    render: (transaction: any) => {
      const isReassignLand = Boolean(transaction?.type === "reassign_land");
      const userImage =
        transaction?.type === EtTransactionType?.TRUST_DISMISSED ||
        transaction?.type === EtTransactionType?.TRUST_FLAGGED_FOR_DISMISSAL ||
        transaction?.type === EtTransactionType?.TRUST_ROLE_GRANTED;

      switch (true) {
        case isReassignLand:
          return (
            <Text numeric contrast medium>
              {transaction.data?.landName || "-"}
            </Text>
          );

        case userImage:
          return (
            <Text numeric contrast medium>
              {transaction.toUserDetails?.name || "-"}
            </Text>
          );

        default:
          return (
            <Text numeric contrast medium>
              {transaction.toUserDetails?.name || "-"}
            </Text>
          );
      }
    },
  };
  const adminColSchema = [
    adminThumbCol,
    orgDetails,
    hashCol,
    transactionTypeCol,
    transactionFromCol,
    transactionToCol,
    transactionValueCol,
  ];

  // const portfolioGuardianColSchema = [
  //   ...baseLandsSchema,
  //   scoreCol,
  //   guardianCol,
  //   trustCol,
  //   portfolioGuardianStatusCol,
  //   treeMintRateCol,
  // ];

  // const assetsGuardianColSchema = [
  //   ...baseLandsSchema,
  //   scoreCol,
  //   ownerCol,
  //   trustCol,
  //   assetsGuardianStatusCol,
  //   treeMintRateCol,
  // ];

  // const trusteeVerifiedColSchema = [
  //   ...baseLandsSchema,
  //   scoreCol,
  //   ownerCol,
  //   guardianCol,
  //   // publicStatusCol,
  //   {
  //     id: "status",
  //     label: "Status",
  //     style: { width: "170px" },
  //     render: (data: {
  //       verifications: string | any[];
  //       status: EcoTokenStatus;
  //       etNft: { ownerAddress: any; minterAddress: any; easementActivated: { contractStart: moment.MomentInput } };
  //       minterAddress: any;
  //       meta: { contractPeriod: moment.DurationInputArg1 };
  //       timestamps: { verifiedAt: moment.MomentInput };
  //       id: any;
  //     }) => {
  //       const lastVrf = data.verifications?.[0];
  //       const standardized = resolveStandardStatus(
  //         {
  //           status: data.status,
  //           etNft: data.etNft as any,
  //           meta: data.meta as any,
  //         },
  //         {
  //           role: "trust",
  //           lastVerificationStatus: (lastVrf?.status as EcoTokenStatus) || null,
  //           approvedLabel: "Approved",
  //         }
  //       );
  //       if (standardized) {
  //         return (
  //           <Text
  //             medium
  //             contrast
  //             capital
  //             sx={{ color: standardized.color, p: 0, display: "flex", alignItems: "center" }}
  //           >
  //             {standardized.showPulse && (
  //               <PulseCircle
  //                 warning={!!standardized.pulseWarning}
  //                 sold={!!standardized.pulseSold && !standardized.pulseWarning}
  //               />
  //             )}
  //             {standardized.label}
  //           </Text>
  //         );
  //       }

  //       console.log("Here", data);
  //       const verification = onVerification(lastVrf.status) || onVerification(data.status);
  //       const isReassignment = onReassignment(lastVrf.status) || onReassignment(data.status);
  //       const reVerification = lastVrf?.status === EcoTokenStatus.RE_VERIFICATION_REQUIRED;
  //       const claimed =
  //         lastVrf?.status === EcoTokenStatus.CLAIMED || data.status === EcoTokenStatus.VERIFICATION_REQUIRED;
  //       const verificationStarted =
  //         lastVrf?.createdAt && moment(lastVrf.createdAt).add(VERIFICATION_INTERVAL_VALUE, VERIFICATION_INTERVAL_UNIT);
  //       const sold =
  //         !!data.etNft?.ownerAddress && !!data.etNft.minterAddress && data.etNft.ownerAddress !== data.minterAddress;
  //       const minted = data.status === EcoTokenStatus.VERIFIED;
  //       const approved = data.status === EcoTokenStatus.APPROVED;
  //       const expired =
  //         sold &&
  //         moment(data.etNft.easementActivated?.contractStart).add(data.meta.contractPeriod, TERM_UNIT).isBefore();
  //       const textColor = expired
  //         ? palette.primary.main
  //         : lastVrf.status === EcoTokenStatus.DRAFT
  //         ? palette.error.main
  //         : lastVrf.status === EcoTokenStatus.CLAIMED
  //         ? palette.success.main
  //         : onVerification(lastVrf.status)
  //         ? palette.secondary.main
  //         : lastVrf.status === EcoTokenStatus.APPROVED
  //         ? sold
  //           ? palette.success.main
  //           : palette.primary.main
  //         : palette.secondary.main;

  //       const active =
  //         minted && data.etNft?.easementActivated?.contractStart
  //           ? moment(data.etNft.easementActivated.contractStart)
  //           : null;

  //       const text = expired ? (
  //         "Expired"
  //       ) : isReassignment ? (
  //         "Reassignment"
  //       ) : verification ? (
  //         <>
  //           <PulseCircle warning={reVerification} />
  //           {reVerification ? "Required" : claimed ? "Claimed" : "Verification"}
  //         </>
  //       ) : sold ? (
  //         <>
  //           <PulseCircle sold hide={expired} />
  //           {expired ? "Expired" : "Active"}
  //         </>
  //       ) : lastVrf.status === EcoTokenStatus.REJECTED ? (
  //         "Rejected"
  //       ) : approved ? (
  //         "Approved"
  //       ) : (
  //         "Verified"
  //       );

  //       const single = data.verifications.length === 1;

  //       const last =
  //         minted && (active || sold) && lastVrf.status === EcoTokenStatus.APPROVED
  //           ? data.timestamps?.verifiedAt
  //             ? moment(data.timestamps?.verifiedAt)
  //             : null
  //           : null;

  //       let nextVerification = null;

  //       if (single) {
  //         if (active) {
  //           nextVerification = active.add(VERIFICATION_INTERVAL_VALUE, VERIFICATION_INTERVAL_UNIT);
  //         } else if (sold) {
  //           nextVerification = moment(data.timestamps?.verifiedAt).add(
  //             VERIFICATION_INTERVAL_VALUE,
  //             VERIFICATION_INTERVAL_UNIT
  //           );
  //         }
  //       } else {
  //         if (last) {
  //           nextVerification = last.add(VERIFICATION_INTERVAL_VALUE, VERIFICATION_INTERVAL_UNIT);
  //         }
  //       }

  //       const soon = nextVerification && nextVerification.isBefore(moment().add(3, "h"));
  //       const pastDue = nextVerification && nextVerification.isBefore(moment());

  //       if (active) {
  //         console.log(`Contract started for [${data.id}] at ${moment(active).format(DATE_FORMAT)}`, {
  //           single,
  //           status: lastVrf.status,
  //           token: data.status,
  //         });
  //       } else {
  //         console.log(`Contract not started for [${data.id}]`, {
  //           single,
  //           status: lastVrf.status,
  //           token: data.status,
  //         });
  //       }

  //       const tenseText = pastDue ? "due" : "is";

  //       if (nextVerification) {
  //         console.log({ expired, reVerification, nextVerification, active });
  //       }

  //       return (
  //         <Tooltip
  //           arrow={true}
  //           placement="bottom"
  //           disableHoverListener={expired || (!reVerification && !nextVerification && !active)}
  //           title={
  //             nextVerification
  //               ? `Next verification ${tenseText} ` +
  //                 (pastDue ? moment(nextVerification).toNow() : moment(nextVerification).fromNow())
  //               : verificationStarted
  //               ? "Should be completed " + verificationStarted.fromNow()
  //               : false
  //           }
  //         >
  //           <span style={{ cursor: "pointer" }}>
  //             <Text
  //               mono
  //               sx={{ flexDirection: "column", color: pastDue ? "error.main" : soon ? "action.pulse" : "text.primary" }}
  //             >
  //               <Text medium contrast capital sx={{ color: textColor, p: 0, display: "flex", alignItems: "center" }}>
  //                 {text}
  //               </Text>
  //               {!expired && !verification && nextVerification && <Text>{nextVerification.format(DATE_FORMAT)}</Text>}
  //               {reVerification && (
  //                 <Text sx={{ color: "text.disabled" }}>{verificationStarted.format(DATE_FORMAT)}</Text>
  //               )}
  //             </Text>
  //           </span>
  //         </Tooltip>
  //       );
  //     },
  //   },
  //   treeMintRateCol,
  //   // auctionStatusCol,
  //   // easementActivatedCol,
  //   valueCol,
  // ];

  // Hoisted so it can be handed to <Profile panel> — it renders inside the image
  // surface, while the stats and table below stay on the page ground.
  const hero = (
    <UserWidget
      sx={{
        // The land image and dark wash now live on the ProfileSurface around this
        // widget, so the row of tabs shares them. `background` (not backgroundColor)
        // also clears Paper's dark-mode overlay image.
        background: "transparent",
        color: "white",
        border: "none",
        minHeight: { xs: "230px", md: "244px" }, // Increased height for Hero section
      }}
    >
      <MainWidgetContainer
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "start",
          alignItems: { xs: "flex-start", md: "flex-start" },
          height: "100%",
          width: { xs: "100%", md: "auto" },
          gap: 2,
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: { xs: "14px", md: "18px" },
            width: "100%",
            alignItems: { xs: "flex-start", md: "flex-start" },
          }}
        >
          <Text
            sx={{
              fontSize: { xs: "14px", md: "26px" }, // Large text from Figma
              lineHeight: 1.2,
              textTransform: "capitalize",
              fontWeight: { xs: "900", md: "700" },
            }}
          >
            {isGuardian
              ? "Guardian"
              : isPioneer
              ? "Pioneer"
              : isTrust
              ? "Trustee"
              : isGlobalTrustAdmin
              ? "Global Trust Admin"
              : isAdmin
              ? "Trust Admin"
              : "Patron"}{" "}
            since {moment(user?.createdAt).format("MMM YYYY")}
          </Text>
          {user.organization?.name && user.trusteeStatus === "verified" && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mt: 1,
                cursor: "pointer",
                opacity: 0.9,
                "&:hover": { opacity: 1 },
              }}
              onClick={() => setOrgModalOpen(true)}
            >
              <Text bold sx={{ fontSize: "14px" }}>
                Organization:
              </Text>
              <Text underline bold sx={{ fontSize: "14px" }}>
                {user?.organization?.name}
              </Text>
            </Box>
          )}
          {/* Mobile-Only: Profile Visuals Row */}
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              flexDirection: "row",
              gap: "14px",
              mt: "12px",
              mb: "24px",
            }}
          >
            {user?.profilePicture && (
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: "50%",
                  overflow: "hidden",
                  border: "0.5px solid rgba(255,255,255,0.2)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: "rgba(0,0,0,0.2)",
                  "& img": {
                    width: "100% !important",
                    height: "100% !important",
                    objectFit: user?.role === "admin" ? "contain" : "cover",
                    scale: user?.role === "admin" ? "0.85" : "1",
                    borderRadius: user?.role === "admin" ? "0%" : "50%",
                  },
                }}
              >
                <ProfilePicture user={user} />
              </Box>
            )}
            <Box
              sx={{
                width: 60,
                height: 60,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {isGuardian ? (
                <GuardianIcon sx={{ fontSize: "60px", color: "#BC92F8" }} />
              ) : isPioneer ? (
                <PioneerIcon sx={{ fontSize: "60px", color: "#BC92F8" }} />
              ) : isTrust ? (
                <TrusteeIcon sx={{ fontSize: "60px", color: "#BC92F8" }} />
              ) : isGlobalTrustAdmin ? (
                <GlobalTrustAdminIcon sx={{ fontSize: "60px", color: "#BC92F8" }} />
              ) : isAdmin ? (
                <TrustAdminIcon sx={{ fontSize: "60px", color: "#BC92F8" }} />
              ) : (
                <PatronIcon sx={{ fontSize: "60px", color: "#BC92F8" }} />
              )}
            </Box>
          </Box>

          {/* Desktop Address Link */}
          <AddressLink
            address={user?.address}
            type="plain"
            copy
            long={true}
            sx={{
              display: { xs: "none", md: "flex" },
              fontSize: "14px",
              color: "black",
              backgroundColor: "rgba(255, 255, 255, 0.5)",
              padding: "16px 24px",
              borderRadius: "20px",
              width: "fit-content",
              "a.plainAddress": {
                color: "black",
                fontSize: { xs: "14px", md: "14px" },
                fontWeight: "700",
              },
            }}
          />

          {/* Mobile Address Link */}
          <AddressLink
            address={user?.address}
            type="plain"
            copy
            long={false}
            sx={{
              display: { xs: "flex", md: "none" },
              fontSize: "14px",
              color: "black",
              backgroundColor: "rgba(255, 255, 255, 0.5)",
              padding: "12px 16px",
              borderRadius: "20px",
              width: "100%",
              justifyContent: "space-between",
              "a.plainAddress": {
                color: "black",
                fontSize: { xs: "14px", md: "14px" },
                fontWeight: "700",
              },
            }}
          />
        </Box>
      </MainWidgetContainer>

      <MainWidgetContainer
        sx={{
          display: { xs: "none", md: "flex" }, // Hide on mobile, show on Desktop
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 4,
          width: "auto",
          position: "relative",
        }}
      >
        {/* Visual Elements: Profile Pic + Role Icon */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 2, md: 4 },
          }}
        >
          {user?.profilePicture && (
            <Box
              sx={{
                width: { xs: 80, md: 180 },
                height: { xs: 80, md: 180 },
                borderRadius: "50%",
                overflow: "hidden",
                border: "0.5px solid rgba(255,255,255,0.2)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "rgba(0,0,0,0.2)",
                "& img": {
                  width: "100% !important",
                  height: "100% !important",
                  objectFit: user?.role === "admin" ? "cover" : "cover",
                  scale: user?.role === "admin" ? "1" : "1",
                  borderRadius: user?.role === "admin" ? "50%" : "50%",
                },
              }}
            >
              <ProfilePicture user={user} />
            </Box>
          )}

          {/* Display Role Icon */}
          <Box
            sx={{
              width: { xs: 80, md: 180 },
              height: { xs: 80, md: 180 },
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {isGuardian ? (
              <GuardianIcon sx={{ fontSize: { xs: "80px", md: "180px" }, color: "#BC92F8" }} />
            ) : isPioneer ? (
              <PioneerIcon sx={{ fontSize: { xs: "80px", md: "180px" }, color: "#BC92F8" }} />
            ) : isTrust ? (
              <TrusteeIcon sx={{ fontSize: { xs: "80px", md: "180px" }, color: "#BC92F8" }} />
            ) : isGlobalTrustAdmin ? (
              <GlobalTrustAdminIcon sx={{ fontSize: { xs: "80px", md: "180px" }, color: "#BC92F8" }} />
            ) : isAdmin ? (
              <TrustAdminIcon sx={{ fontSize: { xs: "80px", md: "180px" }, color: "#BC92F8" }} />
            ) : (
              <PatronIcon sx={{ fontSize: { xs: "80px", md: "180px" }, color: "#BC92F8" }} />
            )}
          </Box>
        </Box>
      </MainWidgetContainer>
    </UserWidget>
  );

  return (
    <Profile
      surface="image"
      image={latestLandImage}
      // Keep the row and the hero's silhouette mounted while the data loads, so the
      // tabs never blink out and the image just fades in when it arrives.
      panel={isLoading ? <Box sx={{ minHeight: { xs: "230px", md: "244px" } }} /> : hero}
    >
      {isLoading ? (
        // <Box
        //   sx={{
        //     display: 'flex',
        //     justifyContent: 'center',
        //     alignItems: 'center',
        //     height: '600px',
        //     backgroundColor: "#ccc"
        //   }}
        // >
        //   <CircularProgress />
        // </Box>
        <Container style={{ maxWidth: "100%", height: "100%", borderRadius: "20px" }}>
          <EmptyBox style={{ borderRadius: "20px", height: "60vh" }}>
            <span>🔎</span>
            <span>Data is loading...</span>
          </EmptyBox>
        </Container>
      ) : (
          <>
            
          <Card sx={{ height: "auto !important", border: "none", backgroundColor: "#ccc" }}>
            {isTrust ? (
              <WidgetContainer sx={{ backgroundColor: "#ccc" }}>
                <Widget
                  sx={{
                    borderLeft: 0,
                    border: "none",
                    flexDirection: { xs: "row", md: "row" },
                    borderRadius: { xs: "20px", md: "20px" },
                    backgroundColor: "#BC92F8",
                  }}
                >
                  <WidgetRow
                    sx={{
                      fontSize: { xs: "14px", md: "16px" },
                      fontWeight: 700,
                      fontFamily: "monospace",
                      color: "black !important",
                    }}
                  >
                    COUNTRY
                  </WidgetRow>
                  <WidgetRow sx={{ color: "black !important" }}>
                    <Text medium numeric bold sx={{ color: "black !important" }}>
                      {orgData?.country || "—"}
                    </Text>
                  </WidgetRow>
                </Widget>

                <Widget
                  sx={{
                    border: "none",
                    flexDirection: { xs: "row", md: "row" },
                    borderRadius: { xs: "20px", md: "20px" },
                    backgroundColor: "#CCFF99",
                  }}
                >
                  <WidgetRow
                    sx={{
                      fontSize: { xs: "14px", md: "16px" },
                      fontWeight: 700,
                      fontFamily: "monospace",
                      color: "black !important",
                    }}
                  >
                    LANDS VERIFIED
                  </WidgetRow>
                  <ClickableContainer
                    onClick={() =>
                      (profileData?.portfolio?.totalVerifiedLandsCount || 0) > 0 &&
                      navigator(AppRoutes.Profile.subRoutes.Verification.path)
                    }
                    sx={{
                      cursor: (profileData?.portfolio?.totalVerifiedLandsCount || 0) > 0 ? "pointer" : "default",
                    }}
                  >
                    <WidgetRow sx={{ color: "black !important" }}>
                      <Text medium numeric bold sx={{ color: "black !important" }}>
                        {infoLoading ? "..." : profileData?.portfolio?.totalVerifiedLandsCount}
                      </Text>
                    </WidgetRow>
                  </ClickableContainer>
                </Widget>

                <Widget
                  sx={{
                    border: "none",
                    flexDirection: { xs: "row", md: "row" },
                    borderRadius: { xs: "20px", md: "20px" },
                    alignItems: "center",
                    gap: "18px",
                    backgroundColor: "#53593C",
                  }}
                >
                  <WidgetRow
                    sx={{
                      fontSize: { xs: "14px", md: "16px" },
                      fontWeight: 700,
                      fontFamily: "monospace",
                      color: "black !important",
                    }}
                  >
                    {isMobile ? "VALUE" : "PORTFOLIO VALUE"}
                  </WidgetRow>

                  <WidgetRow sx={{ color: "black !important" }}>
                    <Text medium numeric bold sx={{ wordBreak: "break-all", color: "black !important" }}>
                      ${toReadable(profileData?.portfolio?.portfolioSum)}
                    </Text>
                  </WidgetRow>
                </Widget>

                <Widget
                  sx={{
                    border: "none",
                    flexDirection: { xs: "row", md: "row" },
                    borderRadius: { xs: "20px", md: "20px" },
                    backgroundColor: "#959A80",
                  }}
                >
                  <WidgetRow
                    sx={{
                      fontSize: { xs: "14px", md: "16px" },
                      fontWeight: 700,
                      fontFamily: "monospace",
                      color: "black !important",
                    }}
                  >
                    {isMobile ? "TX" : "TRANSACTIONS"}
                  </WidgetRow>
                  <WidgetRow sx={{ color: "black !important" }}>
                    <Text medium numeric bold sx={{ color: "black !important" }}>
                      {transactions}
                    </Text>
                  </WidgetRow>
                </Widget>
              </WidgetContainer>
            ) : isAdmin ? (
              <WidgetContainer sx={{ backgroundColor: "#ccc" }}>
                <Widget
                  sx={{
                    borderLeft: 0,
                    border: "none",
                    flexDirection: { xs: "row", md: "row" },
                    borderRadius: { xs: "20px", md: "20px" },
                    backgroundColor: "#BC92F8",
                  }}
                >
                  <WidgetRow
                    sx={{
                      fontSize: { xs: "14px", md: "16px" },
                      fontWeight: 700,
                      fontFamily: "monospace",
                      color: "black !important",
                    }}
                  >
                    COUNTRY
                  </WidgetRow>
                  <WidgetRow sx={{ color: "black !important" }}>
                    <Text medium numeric bold sx={{ color: "black !important" }}>
                      {!user?.country && Number(user?.countryCode) === 0 ? "Global" : user?.country}
                    </Text>
                  </WidgetRow>
                </Widget>
                <Widget
                  sx={{
                    border: "none",
                    flexDirection: { xs: "row", md: "row" },
                    borderRadius: { xs: "20px", md: "20px" },
                    backgroundColor: "#CCFF99",
                  }}
                >
                  <WidgetRow
                    sx={{
                      fontSize: { xs: "14px", md: "16px" },
                      fontWeight: 700,
                      fontFamily: "monospace",
                      color: "black !important",
                    }}
                  >
                    TRUSTS VERIFIED
                  </WidgetRow>
                  <WidgetRow sx={{ color: "black !important" }}>
                    <Text medium numeric bold sx={{ color: "black !important" }}>
                      {adminVerifiedTrusts.length || 0}
                      {/* <CurrencyLabel sx={{ color: "black" }} /> */}
                    </Text>
                  </WidgetRow>
                </Widget>

                <Widget
                  sx={{
                    border: "none",
                    flexDirection: { xs: "row", md: "row" },
                    borderRadius: { xs: "20px", md: "20px" },
                    backgroundColor: "#53593C",
                  }}
                >
                  <WidgetRow
                    sx={{
                      fontSize: { xs: "14px", md: "16px" },
                      fontWeight: 700,
                      fontFamily: "monospace",
                      color: "black !important",
                    }}
                  >
                    RECENT
                  </WidgetRow>

                  <WidgetRow sx={{ color: "black !important" }}>
                    <ClickableContainer>
                      <AddressLink
                        sx={{
                          width: "100%",
                          "& a.userAddress, & a.trustAddress": { textDecoration: "none" },
                          "& a.userAddress:hover, & a.trustAddress:hover": { textDecoration: "none" },
                          "& a": {
                            color: "#000 !important",
                            fontWeight: "bold",
                            fontSize: { xs: "14px", sm: "14px" },
                          },
                        }}
                        address={adminVerifiedTrusts[0]}
                        small
                        type={"user"}
                      />
                    </ClickableContainer>
                  </WidgetRow>
                </Widget>
                <Widget
                  sx={{
                    border: "none",
                    flexDirection: { xs: "row", md: "row" },
                    borderRadius: { xs: "20px", md: "20px" },
                    backgroundColor: "#959A80",
                  }}
                >
                  <WidgetRow
                    sx={{
                      fontSize: { xs: "14px", md: "16px" },
                      fontWeight: 700,
                      fontFamily: "monospace",
                      color: "black !important",
                    }}
                  >
                    {isMobile ? "TX" : "TRANSACTIONS"}
                  </WidgetRow>
                  <WidgetRow sx={{ color: "black !important" }}>
                    <Text medium numeric bold sx={{ color: "black !important" }}>
                      {transactions}
                    </Text>
                  </WidgetRow>
                </Widget>
              </WidgetContainer>
            ) : (
              <WidgetContainer sx={{ backgroundColor: "#ccc" }}>
                <Widget
                  sx={{
                    borderLeft: 0,
                    border: "none",
                    flexDirection: { xs: "row", md: "row" },
                    borderRadius: { xs: "20px", md: "20px" },
                    backgroundColor: "#BC92F8",
                  }}
                >
                  <WidgetRow
                    sx={{
                      fontSize: { xs: "14px", md: "16px" },
                      fontWeight: 700,
                      fontFamily: "monospace",
                      color: "black !important",
                    }}
                  >
                    TR3
                  </WidgetRow>
                  <WidgetRow sx={{ color: "black !important" }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: { xs: "end", md: "start" },
                        gap: 0.5,
                      }}
                    >
                      <Text medium numeric bold sx={{ color: "black !important" }}>
                        {treeBalance}
                        {/* <CurrencyLabel sx={{ color: "black" }} /> */}
                      </Text>
                      {rewardsClaimed24h && rewardsClaimed24h}
                    </Box>
                  </WidgetRow>
                </Widget>

                <Widget
                  sx={{
                    border: "none",
                    flexDirection: { xs: "row", md: "row" },
                    borderRadius: { xs: "20px", md: "20px" },
                    alignItems: "center",
                    backgroundColor: "#CCFF99",
                  }}
                >
                  <WidgetRow
                    sx={{
                      fontSize: { xs: "14px", md: "16px" },
                      fontWeight: 700,
                      fontFamily: "monospace",
                      color: "black !important",
                    }}
                  >
                    PORTFOLIO
                  </WidgetRow>
                  <ClickableContainer
                    onClick={() => landsBought > 0 && navigator(AppRoutes.Profile.subRoutes.Collection.path)}
                    sx={{ cursor: landsBought > 0 ? "pointer" : "default" }}
                  >
                    <WidgetRow sx={{ color: "black !important" }}>
                      <Text medium numeric bold sx={{ color: "black !important" }}>
                        {landsBought}
                      </Text>
                    </WidgetRow>
                  </ClickableContainer>
                </Widget>

                <Widget
                  sx={{
                    height: "100%",
                    justifyContent: "start",
                    flexDirection: { xs: "row", md: "row" },
                    borderRadius: { xs: "20px", md: "20px" },
                    border: "none",
                    alignItems: "center",
                    backgroundColor: "#53593C",
                  }}
                >
                  <WidgetRow
                    sx={{
                      fontSize: { xs: "14px", md: "16px" },
                      fontWeight: 700,
                      fontFamily: "monospace",
                      color: "black !important",
                    }}
                  >
                    ASSETS
                  </WidgetRow>

                  <ClickableContainer
                    onClick={() =>
                      landsScanned > 0 && navigator(`${AppRoutes.Profile.subRoutes.Collection.path}?activeTab=lands`)
                    }
                    sx={{ cursor: landsScanned > 0 ? "pointer" : "default" }}
                  >
                    <WidgetRow sx={{ color: "black !important" }}>
                      <Text medium numeric bold sx={{ color: "black !important" }}>
                        {landsScanned}
                      </Text>
                    </WidgetRow>
                  </ClickableContainer>
                </Widget>

                <Widget
                  sx={{
                    border: "none",
                    flexDirection: { xs: "row", md: "row" },
                    borderRadius: { xs: "20px", md: "20px" },
                    backgroundColor: "#959A80",
                  }}
                >
                  <WidgetRow
                    sx={{
                      fontSize: { xs: "14px", md: "16px" },
                      fontWeight: 700,
                      fontFamily: "monospace",
                      color: "black !important",
                    }}
                  >
                    {isMobile ? "TX" : "TRANSACTIONS"}
                  </WidgetRow>
                  <WidgetRow sx={{ color: "black !important" }}>
                    <Text medium numeric bold sx={{ color: "black !important" }}>
                      {transactions}
                    </Text>
                  </WidgetRow>
                </Widget>
              </WidgetContainer>
            )}

            <OrgPopUp
              open={orgModalOpen}
              onClose={() => setOrgModalOpen(false)}
              orgData={orgData}
              orgLands={orgLands}
            />
          </Card>

          <TableWithTabs sx={{ backgroundColor: "back.table", borderRadius: "20px" }}>
            <TabsRow sx={{ borderRadius: "20px" }}>
              <FilterBlock
                sx={{
                  padding: { xs: "18px", sm: "24px" },
                  borderRadius: "20px",
                  "@media (min-width: 600px)": {
                    display: "flex",
                    overflowX: "auto",
                    whiteSpace: "nowrap",
                  },
                  "@media (max-width: 599px)": {
                    flexDirection: "row",
                  },
                }}
              >
                {!isAdmin && (
                  <ActionBtn
                    className={activeTab === "transactions" ? "active" : ""}
                    variant={activeTab === "transactions" ? "contained" : "outlined"}
                    size="medium"
                    onClick={() => setActiveTab("transactions")}
                  >
                    Transactions
                  </ActionBtn>
                )}

                {!!landsBought && !isTrust && !isAdmin && (
                  <ActionBtn
                    className={activeTab === "portfolio" ? "active" : ""}
                    variant={activeTab === "portfolio" ? "contained" : "outlined"}
                    size="medium"
                    onClick={() => setActiveTab("portfolio")}
                  >
                    Portfolio
                  </ActionBtn>
                )}
                {(!!landsScanned || isAdmin) && (
                  <ActionBtn
                    className={activeTab === "lands" ? "active" : ""}
                    variant={activeTab === "lands" ? "contained" : "outlined"}
                    size="medium"
                    onClick={() => setActiveTab("lands")}
                  >
                    {isTrust ? "Verified" : isAdmin ? "Transactions" : "Assets"}
                  </ActionBtn>
                )}
                {isAdmin && adminVerifiedTrusts.length > 0 && (
                  <ActionBtn
                    className={activeTab === "portfolio" ? "active" : ""}
                    variant={activeTab === "portfolio" ? "contained" : "outlined"}
                    size="medium"
                    onClick={() => setActiveTab("portfolio")}
                  >
                    Verified
                  </ActionBtn>
                )}
              </FilterBlock>
            </TabsRow>

            {activeTab === "portfolio" && !isTrust && !isAdmin && (
              <Grid container sx={{ width: "100%" }}>
                <TableComponent
                  mobileTitle="Portfolio"
                  batch={5}
                  hideTitle
                  totalItems={landsBought}
                  title="Portfolio"
                  customData={getCachedData("boughtLands") || []}
                  apiURL=""
                  schema={isMobile ? MobileEcoBankLandsSchema : MobileEcoBankLandsSchema}
                />
              </Grid>
            )}

            {activeTab === "lands" && (
              <Grid container sx={{ width: "100%" }}>
                <TableComponent
                  mobileTitle={isTrust ? "Verified Lands" : isAdmin ? "Transactions" : "Assets"}
                  batch={5}
                  hideTitle
                  totalItems={isAdmin ? transactions : landsScanned}
                  title={isTrust ? "Verified Lands" : isAdmin ? "Transactions" : "Assets"}
                  customData={
                    isAdmin
                      ? getCachedData("transactionData") || []
                      : isTrust
                      ? getCachedData("trusteeTokens") || []
                      : getCachedData("mintedTokens") || []
                  }
                  apiURL=""
                  schema={
                    isAdmin
                      ? adminColSchema
                      : isTrust
                      ? isMobile
                        ? MobileEcoBankLandsSchema
                        : MobileEcoBankLandsSchema
                      : isGuardian
                      ? isMobile
                        ? MobileEcoBankLandsSchema
                        : MobileEcoBankLandsSchema
                      : isMobile
                      ? MobileEcoBankLandsSchema
                      : MobileEcoBankLandsSchema
                  }
                />
              </Grid>
            )}

            {activeTab === "transactions" && (
              <Grid container sx={{ width: "100%" }}>
                <TableComponent
                  mobileTitle={isAdmin ? "Latest Transactions" : "Wallet transactions"}
                  batch={isAdmin ? 5 : 5}
                  hideTitle
                  totalItems={(getCachedData("transactionData") || []).length}
                  title={isAdmin ? "Latest Transactions" : "Wallet transactions"}
                  customData={getCachedData("transactionData") || []}
                  apiURL=""
                  schema={isMobile ? TransactionMobileSchema : TransactionsSchema}
                />
              </Grid>
            )}

            {isAdmin && adminVerifiedTrusts.length > 0 && activeTab === "portfolio" && (
              <Grid container sx={{ width: "100%" }}>
                <TableComponent
                  mobileTitle="Portfolio"
                  batch={5}
                  hideTitle
                  totalItems={verifiedByData.length}
                  title="Portfolio"
                  customData={verifiedByData}
                  apiURL=""
                  schema={trusteeSchema
                    .filter((col) => col.id !== "actions")
                    .map((col) =>
                      col.id === "name" && col.label === "Name"
                        ? {
                            ...col,
                            render: (data: any, index?: number) => (
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                                {col.render?.(data, index)}
                                {data?.role && (
                                  <Text
                                    mono
                                    sx={{
                                      fontFamily: "monospace",
                                      textTransform: "uppercase",
                                      fontSize: "16px",
                                      color: "#565656",
                                    }}
                                  >
                                    {data.role}
                                  </Text>
                                )}
                              </Box>
                            ),
                          }
                        : col
                    )}
                />
              </Grid>
            )}
          </TableWithTabs>
        </>
      )}
    </Profile>
  );
};

// const Col = (props: GridProps) => <Grid {...props} container alignItems="start" direction="column" />;

export const Widget = styled(Card)`
  flex: 1;
  background: ${({ theme }) => theme.palette.back.darkGreen};
  color: white;
  // border: 1px solid ${({ theme }) => theme.palette.border.dark};
  border: none;
  justify-content: space-between;
  display: flex;
  flex-direction: column;

  ${({ theme }) => ({
    [theme.breakpoints.up("xs")]: {
      minHeight: "55px",
      padding: "18px",
      justifyContent: "space-between",
      flexDirection: "row-reverse",
    },
    [theme.breakpoints.up("md")]: {
      // minHeight: "110px",
      padding: "24px",
      flexDirection: "column",
    },
  })}
  &:not(:first-of-type) {
    border-left: none;
  }

  @media (max-width: 560px) {
    border-right: 0;
  }
`;

// const TreeWidget = styled(Widget)`
//   justify-content: center;
//   ${({ theme }) => ({
//     [theme.breakpoints.up("xs")]: {
//       minHeight: "55px",
//       padding: "12px",
//       justifyContent: "space-between",
//       flexDirection: "row-reverses",
//     },
//     [theme.breakpoints.up("md")]: {
//       minHeight: "110px",
//       padding: "0 24px",
//       justifyContent: "center",
//       flexDirection: "column",
//     },
//   })}
// `;

export const UserWidget = styled(Widget)`
  justify-content: start;
  flex-direction: row;
  height: auto;
  min-height: auto;
  width: 100%;

  ${({ theme }) => ({
    [theme.breakpoints.up("xs")]: {
      padding: "18px",
      flexDirection: "column",
      gap: "24px",
      alignItems: "flex-start",
    },
    [theme.breakpoints.up("md")]: {
      padding: "26px 26px",
      flexDirection: "row",
      alignItems: "center",
      gap: "40px",
      justifyContent: "space-between",
    },
  })}
`;

export const MainWidget = styled(Widget)`
  justify-content: center;

  ${({ theme }) => ({
    [theme.breakpoints.up("xs")]: {
      minHeight: "55px",
      padding: "18px",
      justifyContent: "space-between",
      flexDirection: "column",
    },
    [theme.breakpoints.up("md")]: {
      padding: "24px",
    },
  })}
`;

export const WidgetContainer = styled("div")`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;

  @media (max-width: 992px) {
    grid-template-columns: 1fr 1fr;

    & > div {
      &:nth-of-type(even) {
        border-left: 0;
        border-right: 0;
      }
    }
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr 1fr;

    & > div {
      &:nth-of-type(even) {
        border-left: 0;
        border-right: 0;
      }
    }
  }

  button {
    min-width: max-content;
    max-width: max-content;
  }
`;

export const MainWidgetContainer = styled("div")``;

export const WidgetRow = styled("div")`
  display: flex;
  text-transform: uppercase;
  font-family: "monospace", monospace;
  flex-direction: row;
  align-items: center;

  & > svg {
    margin-right: 10px;
  }

  span {
    span {
      font-weight: normal;
    }
  }
`;

export const TrusteeIcon2 = createSvgIcon(
  // eslint-disable-next-line max-len
  <svg xmlns="http://www.w3.org/2000/svg" width="103" height="128" viewBox="0 0 103 128" fill="none">
    <path
      d="M45.0157 83.3921H57.8596V67.4076H73.9455V54.6447H57.8596V38.6602H45.0157V54.6447H28.9298V67.4076H45.0157V83.3921ZM51.5 128C36.5363 124.283 24.1913 115.609 14.4649 101.979C4.8632 88.3485 0 73.4792 0 57.2469V19.2062L51.5 0L103 19.2062V57.2469C103 73.4792 98.1368 88.3485 88.5351 101.979C78.9334 115.609 66.5884 124.283 51.5 128ZM51.5 118.087C63.845 113.998 73.9455 106.439 81.6767 95.1636C89.408 83.8877 93.3983 71.2488 93.3983 57.3708V26.0213L51.5 10.4085L9.60169 26.0213V57.3708C9.60169 71.3727 13.4673 83.8877 21.3233 95.1636C29.0545 106.439 39.155 114.122 51.5 118.087Z"
      fill="#000"
    />
  </svg>,
  "Trustee2"
);

export default Dashboard;
